

import {
    IonIcon,
    IonContent,
    IonHeader,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonButton,
    IonItem,
    IonLabel,
    IonFooter,
    modalController
} from '@ionic/vue';
import {computed, defineComponent, onBeforeMount, ref} from "vue";
import { closeOutline } from 'ionicons/icons';
import {GET} from "@/services/HTTP";
import {ImageGroup} from "@/models/ImageGroupModel";
import {Image} from "@/models/ImageModel";
import {ImageGroupId} from "@/models/ImageGroupTypes";
import {useStore} from "vuex";
import DirectionIndicator from "@/components/DirectionIndicator.vue";

export default defineComponent({
    name: 'ImageSelectModal',
    props: {
        title: {
            type: String,
            default: ''
        },
        groupId: {
            type: String,
            default: ''
        }
    },
    components: {
        DirectionIndicator,
        IonIcon,
        IonContent,
        IonHeader,
        IonPage,
        IonTitle,
        IonToolbar,
        IonButtons,
        IonButton,
        IonItem,
        IonLabel,
        IonFooter
    },
    setup(props: any) {
        const store = useStore();
        const language = computed(() => store.getters['app/language']);
        const selectedAppInstanceId = computed(() => store.getters['app/selectedAppInstanceId'] as string);
        const imageGroups = ref([] as ImageGroup[]);
        const images = ref([] as Image[]);
        const showImageSelection = ref(false);
        const currentGroupName = ref('');
        const imagePreviewUrls = ref<Record<string, string>>({});
        const isImage = computed(() => props.groupId === ImageGroupId.JourneyImages);

        const revokePreviewUrls = () => {
            Object.values(imagePreviewUrls.value).forEach(url => URL.revokeObjectURL(url));
            imagePreviewUrls.value = {};
        };

        onBeforeMount(async () => {
            const params = new URLSearchParams({ published: 'true' });
            const aid = selectedAppInstanceId.value;
            if (aid)
                params.set('appInstanceId', aid);
            imageGroups.value = await GET('/imageGroups/' + props.groupId + '?' + params.toString(), false);
        });

        const close = () => {
            revokePreviewUrls();
            modalController.dismiss();
        };

        const selectImage = (image: Image) => {
            // Use preview blob URL as data when image has none (e.g. My Icons from S3) so callers can display it
            const previewUrl = image.data || imagePreviewUrls.value[image.id ?? ''] || '';
            const dataToPass: Image = {
                id: image.id,
                data: previewUrl,
                filename: image.filename ?? '',
                imageGroupId: (image as { imageGroupId?: string }).imageGroupId ?? ''
            };
            modalController.dismiss(dataToPass);
            // Don't revoke blob URLs here – parent needs the URL for display; revoke on close/back only
        };

        const goBackToGroups = () => {
            revokePreviewUrls();
            images.value = [];
            showImageSelection.value = false;
            currentGroupName.value = '';
        };

        const getPreviewUrl = (image: Image): string => {
            if (image.data) return image.data;
            return imagePreviewUrls.value[image.id ?? ''] ?? '';
        };

        const loadGroup = async (group: ImageGroup) => {
            currentGroupName.value = group.name;
            const imgs = group.images ?? [];
            images.value = imgs;
            showImageSelection.value = true;
            revokePreviewUrls();
            const urls: Record<string, string> = {};
            await Promise.all(
                imgs
                    .filter(img => !img.data && img.id)
                    .map(async (img) => {
                        try {
                            const res = await GET('/images/' + img.id + '/src', true) as { blob: Blob };
                            urls[img.id!] = URL.createObjectURL(res.blob);
                        } catch {
                            // ignore failed preview load
                        }
                    })
            );
            imagePreviewUrls.value = urls;
        };

        return {
            closeOutline,
            close,
            selectImage,
            goBackToGroups,
            getPreviewUrl,
            imageGroups,
            images,
            showImageSelection,
            currentGroupName,
            loadGroup,
            language
        }
    }
});
